/* ADG Collection — product-grid loading behaviour.
 *
 * This is the PAGINATED (server-rendered) grid used by product collections
 * like Art Prints. The artwork view for art collections is adg-artgrid.js;
 * the two share the same section settings so both feel identical:
 *   - skeleton shimmer per card until its image decodes
 *   - loads ahead of the scroll instead of waiting for a button press
 *   - the button remains a real next-page link, so it works without JS
 */
(function () {
  'use strict';

  var grid = document.querySelector('[data-adg-coll-grid]');
  if (!grid) return;

  var btn = document.querySelector('[data-adg-loadmore]');
  var sentinel = document.querySelector('[data-adg-coll-sentinel]');

  var mode = grid.dataset.loadBehaviour || 'endless';
  // Search (bottom of this file) re-applies its filter to every batch the
  // loader appends, so auto-loaded cards can never bypass an active search.
  var onBatch = null;
  var AUTO = mode === 'endless' || mode === 'auto';
  var AUTO_MAX = mode === 'endless' ? Infinity : (parseInt(grid.dataset.autoBatches, 10) || 8);
  // Ceiling in ITEMS. 'endless' must still stop somewhere: an art collection
  // tops out around 640, but /collections/all is ~15,800 products and would
  // otherwise auto-load the entire catalogue onto a phone.
  var MAX_ITEMS = parseInt(grid.dataset.maxAutoItems, 10) || 750;
  var autoUsed = 0;
  var busy = false;

  /* ---- skeletons ---------------------------------------------------------
     Applies to the server-rendered cards on first paint AND to every card
     appended later. Cleared on load OR error so nothing shimmers forever. */
  function skeletonise(root) {
    root.querySelectorAll('.adg-card__media').forEach(function (media) {
      var img = media.querySelector('img');
      if (!img) return;
      if (img.complete && img.naturalWidth > 0) return;   // already cached
      media.classList.add('is-loading');
      function settle() { media.classList.remove('is-loading'); }
      img.addEventListener('load', settle, { once: true });
      img.addEventListener('error', settle, { once: true });
    });
  }
  skeletonise(grid);

  /* ---- placeholder cards -------------------------------------------------
     The artwork view renders its next batch instantly, because the data is
     already in the browser — so there are always grey shimmering cards sitting
     below the shopper and scrolling feels endless. This path can't do that: it
     has to ask the server, and for those few hundred milliseconds there is
     NOTHING underneath. Flick hard and you slam into the bottom of the page,
     wait, and then sixty cards appear at once.

     So reserve the space up front. The moment a page is requested we append a
     full batch of placeholders — same shimmer, same footprint — and swap them
     for the real cards when the HTML lands. */
  var PER_PAGE = grid.querySelectorAll('.adg-card').length || 60;

  function realCards() {
    return grid.querySelectorAll('.adg-card:not(.adg-card--placeholder)').length;
  }

  function addPlaceholders(n) {
    if (grid.querySelector('.adg-card--placeholder')) return;   // already showing
    var frag = document.createDocumentFragment();
    for (var i = 0; i < n; i++) {
      var el = document.createElement('div');
      el.className = 'adg-card adg-card--placeholder';
      el.setAttribute('aria-hidden', 'true');       // decorative, not announced
      var media = document.createElement('div');
      media.className = 'adg-card__media is-loading';
      el.appendChild(media);
      // Title only. The artist line is no longer rendered under a thumbnail
      // (Joel, 2026-08-17), so reserving a second line box here would leave a
      // gap the real card never fills.
      ['adg-card__title'].forEach(function (cls) {
        var s = document.createElement('span');
        s.className = cls;
        s.innerHTML = '&nbsp;';                     // hold the line box open
        el.appendChild(s);
      });
      frag.appendChild(el);
    }
    grid.appendChild(frag);
  }

  function clearPlaceholders() {
    var ph = grid.querySelectorAll('.adg-card--placeholder');
    for (var i = 0; i < ph.length; i++) ph[i].parentNode.removeChild(ph[i]);
  }

  function finished() {
    if (sentinel) sentinel.hidden = true;
  }

  function loadNext(isAuto) {
    if (!btn || busy) return Promise.resolve();
    var url = btn.getAttribute('href');
    if (!url) { finished(); return Promise.resolve(); }
    busy = true;
    btn.setAttribute('aria-busy', 'true');
    addPlaceholders(PER_PAGE);        // grey boxes NOW, real cards when they land

    return fetch(url, { headers: { 'X-Requested-With': 'XMLHttpRequest' } })
      .then(function (r) { if (!r.ok) throw new Error(r.status); return r.text(); })
      .then(function (html) {
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var newGrid = doc.querySelector('[data-adg-coll-grid]');
        clearPlaceholders();          // always, even if the parse found nothing
        if (newGrid) {
          var frag = document.createDocumentFragment();
          newGrid.querySelectorAll('.adg-card').forEach(function (c) {
            frag.appendChild(document.importNode(c, true));
          });
          grid.appendChild(frag);
          skeletonise(grid);
          if (onBatch) onBatch();
        }
        if (isAuto) autoUsed++;

        var nextBtn = doc.querySelector('[data-adg-loadmore]');
        if (nextBtn && nextBtn.getAttribute('href')) {
          btn.setAttribute('href', nextBtn.getAttribute('href'));
          btn.removeAttribute('aria-busy');
          // While auto-loading still has allowance, keep the button out of the
          // way; once spent it comes back so the footer stays reachable and
          // keyboard users keep a real control.
          btn.hidden = AUTO && autoUsed < AUTO_MAX;
        } else {
          if (btn.parentNode) btn.parentNode.removeChild(btn);
          btn = null;
          finished();
        }
        busy = false;
      })
      .catch(function () {
        clearPlaceholders();                 // never strand grey boxes on screen
        window.location.href = url;          // real navigation fallback
      });
  }

  if (btn) {
    btn.addEventListener('click', function (e) { e.preventDefault(); loadNext(false); });
    // Hide it up front only if auto-loading will take over.
    if (AUTO && AUTO_MAX > 0) btn.hidden = true;
  }

  /* ---- load ahead of the scroll -----------------------------------------
     Driven by the shared engine in adg-infinite.js. It re-arms the observer
     after every batch instead of polling the sentinel from a scroll handler,
     which is what used to force a synchronous layout on every frame against
     a grid of thousands of cards — the "scroll, stick, chug-chug-chug".

     Lookahead is much larger here than in the artwork view. A batch there is
     a synchronous render from memory; here it is a network round trip PLUS
     parsing a whole page of HTML, which on a mid-range phone measured ~1.5 s
     end to end. A hard flick covers ~2700 px/s, so anything under ~2500 px
     leaves the shopper sitting at the bottom waiting for the page to land. */
  var LOOKAHEAD = 2500;
  var engine = null;

  if (AUTO && btn && sentinel && window.ADGInfinite) {
    sentinel.hidden = false;
    engine = window.ADGInfinite.create({
      sentinel: sentinel,
      lookahead: LOOKAHEAD,
      progress: realCards,                       // placeholders don't count
      canLoad: function () {
        if (busy || !btn || autoUsed >= AUTO_MAX) return false;
        if (realCards() >= MAX_ITEMS) {
          btn.hidden = false;                    // hand back to the button
          finished();
          return false;
        }
        return true;
      },
      load: function () { return loadNext(true); },
      onStop: function () { if (btn) btn.hidden = false; }
    });
  }

  // No engine means no IntersectionObserver (or auto-load switched off in the
  // theme editor) — leave the real button in charge and park the sentinel.
  if (!engine) {
    if (btn) btn.hidden = false;
    if (sentinel) sentinel.hidden = true;
  }

  /* ---- Search this collection ---------------------------------------------
     The same control the design grids carry, behaving the same way: type and
     the grid narrows instantly. The difference is where the data lives — the
     design grids hold every row in memory; this grid is server-paginated. So
     the first keystroke DRAINS the remaining pages through the exact same
     loadNext() the infinite scroll uses, then filtering is a DOM toggle.
     Liquid only renders the box between 13 and 600 items, so the worst-case
     drain is ten pages — the design pages fetch more than that in one file. */
  var q = document.querySelector('[data-adg-coll-q]');
  if (q) {
    var countEl = document.querySelector('[data-adg-coll-count]');
    var total = countEl ? (parseInt(countEl.dataset.total, 10) || 0) : 0;
    var emptyMsg = null;
    var draining = null;

    function drainAll() {
      if (draining) return draining;
      draining = (function next() {
        if (!btn) return Promise.resolve();
        return loadNext(false).then(next);
      })();
      return draining;
    }

    function applySearch() {
      var term = q.value.trim().toLowerCase();
      var cards = grid.querySelectorAll('.adg-card:not(.adg-card--placeholder)');
      var shown = 0;
      for (var i = 0; i < cards.length; i++) {
        var hit = !term || cards[i].textContent.toLowerCase().indexOf(term) !== -1;
        cards[i].hidden = !hit;
        if (hit) shown++;
      }
      if (countEl) {
        countEl.textContent = term
          ? shown + ' of ' + (total || cards.length) + ' items'
          : (total || cards.length) + ' ' + ((total || cards.length) === 1 ? 'item' : 'items');
      }
      if (!emptyMsg) {
        emptyMsg = document.createElement('p');
        emptyMsg.className = 'adg-coll__empty';
        emptyMsg.hidden = true;
        grid.parentNode.insertBefore(emptyMsg, grid.nextSibling);
      }
      emptyMsg.textContent = 'Nothing here matches “' + q.value.trim() + '”.';
      emptyMsg.hidden = !(term && shown === 0);
    }

    onBatch = applySearch;

    var qTimer = null;
    q.addEventListener('input', function () {
      clearTimeout(qTimer);
      qTimer = setTimeout(function () {
        applySearch();                       // narrow what is loaded, NOW
        if (q.value.trim() && btn) {
          drainAll().then(applySearch);      // then the rest as it lands
        }
      }, 150);
    });
    // The box lives inside the GET form for layout, but it is not a form
    // param — Enter must filter, not reload the page.
    q.addEventListener('keydown', function (e) {
      if (e.key === 'Enter') { e.preventDefault(); applySearch(); }
    });
  }
})();

/* ---- Toolbar: apply filters / sort on change ------------------------------
   The toolbar is a plain GET form, so it works with JS disabled (the browser
   submits on Enter). This just removes the need to press a button, and drops
   empty params so the URL stays clean. */
(function () {
  var form = document.querySelector('[data-adg-coll-form]');
  if (!form) return;
  function apply() {
    Array.prototype.forEach.call(form.querySelectorAll('select'), function (s) {
      s.disabled = (s.value === '');
    });
    form.submit();
  }
  /* WCAG 3.2.2 (On input): a closed select changes value on every arrow key,
     so reloading on 'change' reloaded the page on the first keypress. A pick
     made with the mouse or a touch picker still applies at once; a change made
     with the keyboard waits for Enter (the hint is read via aria-describedby,
     snippets/adg-filter-row.liquid / sections/adg-collection.liquid). */
  var lastKey = 0, pickerKey = 0;
  var initial = {};
  Array.prototype.forEach.call(form.querySelectorAll('select[data-adg-auto]'), function (s) { initial[s.name] = s.value; });
  form.addEventListener('keydown', function (e) {
    var sel = e.target.closest && e.target.closest('select[data-adg-auto]');
    if (!sel) return;
    // Keys inside an open customizable-select picker (Chrome's base-select,
    // assets/adg-controls.css) are a deliberate choice, like a click.
    if (e.target !== sel) { pickerKey = Date.now(); return; }
    if (e.key === 'Enter' && sel.value !== initial[sel.name]) { e.preventDefault(); apply(); return; }
    if (e.key !== 'Tab' && e.key !== 'Shift' && e.key !== 'Enter') lastKey = Date.now();
  });
  form.addEventListener('change', function (e) {
    if (!e.target.matches('[data-adg-auto]')) return;
    var fromPicker = Date.now() - pickerKey < 1500;
    var fromClosedArrows = Date.now() - lastKey < 800;
    if (fromClosedArrows && !fromPicker) return;   // arrows on a closed select: wait for Enter
    apply();
  });
})();
